import React from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@headlessui/react';
import { ExclamationIcon, LogoutIcon } from '@heroicons/react/outline';

// App-wide confirmation prompt for deletes, logout and other actions that are
// hard to undo:
//
//   const confirm = useConfirm();
//   if (!(await confirm({ title: 'Delete cell?', message: '…', confirmLabel: 'Delete' }))) return;
//
// Resolves true when confirmed, false when cancelled (Cancel, Escape or a click
// outside). Without a ConfirmProvider (e.g. a component rendered on its own in a
// test) it falls back to the browser's confirm box.

const ConfirmContext = React.createContext(null);

export const ConfirmProvider = ({ children }) => {
    const [request, setRequest] = React.useState(null);
    const resolverRef = React.useRef(null);
    const confirmButtonRef = React.useRef(null);
    const cancelButtonRef = React.useRef(null);

    const confirm = React.useCallback((options) => new Promise((resolve) => {
        // A new prompt replaces one still open; the old one counts as cancelled.
        if (resolverRef.current) resolverRef.current(false);
        resolverRef.current = resolve;
        setRequest({ tone: 'danger', confirmLabel: 'Confirm', cancelLabel: 'Cancel', ...options });
    }), []);

    const settle = (value) => {
        if (resolverRef.current) resolverRef.current(value);
        resolverRef.current = null;
        setRequest(null);
    };

    const tone = request?.tone || 'danger';
    const Icon = request?.icon === 'logout' ? LogoutIcon : ExclamationIcon;

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <Dialog
                open={Boolean(request)}
                onClose={() => settle(false)}
                // Destructive actions start on Cancel so Enter can't delete by accident.
                initialFocus={tone === 'danger' ? cancelButtonRef : confirmButtonRef}
                className="relative z-[60]"
            >
                <div className="fixed inset-0 bg-gray-900/40" aria-hidden="true" />
                <div className="fixed inset-0 flex items-center justify-center p-4">
                    <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                        <div className="flex items-start gap-4">
                            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tone === 'danger' ? 'bg-red-100 text-red-600' : 'bg-emerald-100 text-emerald-700'}`}>
                                <Icon className="h-6 w-6" aria-hidden="true" />
                            </span>
                            <div className="min-w-0">
                                <Dialog.Title className="text-lg font-semibold text-gray-900">{request?.title}</Dialog.Title>
                                {request?.message && (
                                    <Dialog.Description className="mt-2 whitespace-pre-line text-sm text-gray-600">
                                        {request.message}
                                    </Dialog.Description>
                                )}
                            </div>
                        </div>
                        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button
                                ref={cancelButtonRef}
                                type="button"
                                onClick={() => settle(false)}
                                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50"
                            >
                                {request?.cancelLabel}
                            </button>
                            <button
                                ref={confirmButtonRef}
                                type="button"
                                onClick={() => settle(true)}
                                className={`rounded-lg px-4 py-2 text-sm font-semibold text-white ${tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                            >
                                {request?.confirmLabel}
                            </button>
                        </div>
                    </Dialog.Panel>
                </div>
            </Dialog>
        </ConfirmContext.Provider>
    );
};

ConfirmProvider.propTypes = { children: PropTypes.node };
ConfirmProvider.defaultProps = { children: null };

const browserConfirm = async ({ title, message }) =>
    window.confirm([title, message].filter(Boolean).join('\n\n'));

export function useConfirm() {
    return React.useContext(ConfirmContext) || browserConfirm;
}

// Ready-made prompts so every screen words them the same way.
export const confirmDelete = (confirm, { what, name, consequence }) => confirm({
    title: `Delete ${what}?`,
    message: [`${name ? `"${name}"` : `This ${what}`} will be removed.`, consequence].filter(Boolean).join(' '),
    confirmLabel: `Delete ${what}`,
    tone: 'danger',
});

export const confirmLogout = (confirm) => confirm({
    title: 'Log out?',
    message: "You'll need to sign in again to use Cell Report.",
    confirmLabel: 'Log out',
    tone: 'neutral',
    icon: 'logout',
});
